import {Component, DestroyRef, HostListener, inject, Input, OnDestroy} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe, DatePipe} from "@angular/common";
import {GameAssetSettingsFormComponent} from "../game-asset-settings-form/game-asset-settings-form.component";
import {GameDrawSettingsComponentForm} from "../game-draw-settings-form/game-draw-settings-component-form.component";
import {GameMetadataSettingsFormComponent} from "../game-metadata-settings-form/game-metadata-settings-form.component";
import {GamePaymentSettingsFormComponent} from "../game-payment-settings-form/game-payment-settings-form.component";
import {GeneralGameSettingsForm} from "../general-game-settings-form/general-game-settings-form.component";
import {MatButton, MatFabButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatStep, MatStepLabel, MatStepper} from "@angular/material/stepper";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {IGameGeneralSettingsForm} from "../../../interfaces/new-game/forms/IGameGeneralSettingsForm";
import {IGameDrawSettingsForm} from "../../../interfaces/new-game/forms/IGameDrawSettingsForm";
import {IGamePaymentSettingsForm} from "../../../interfaces/new-game/forms/IGamePaymentSettingsForm";
import {IGameMetadataSettingsForm} from "../../../interfaces/new-game/forms/IGameMetadataSettingsForm";
import {IGameAssetSettingsForm} from "../../../interfaces/new-game/forms/IGameAssetSettingsForm";
import {NewGameService} from "../../../services/new-game.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {SnackbarService} from "../../../services/snackbar.service";
import {DateService} from "../../../services/date.service";
import {IGameMetadataSettings} from "../../../interfaces/new-game/breakdown/IGameMetadataSettings";
import {IGamePaymentSettings} from "../../../interfaces/new-game/breakdown/IGamePaymentSettings";
import {IGameAssetSettings} from "../../../interfaces/new-game/breakdown/IGameAssetSettings";
import {IGameDrawSettings} from "../../../interfaces/new-game/breakdown/IGameDrawSettings";
import {IGameGeneralSettings} from "../../../interfaces/new-game/breakdown/IGameGeneralSettings";
import {catchError, concatMap, debounceTime, defer, EMPTY, finalize, forkJoin, map, Observable, of, ReplaySubject, Subject, tap} from "rxjs";
import {GameStateEnum} from "../../../enum/GameStateEnum";
import {ICompleteNewGameRequest} from "../../../interfaces/new-game/ICompleteNewGameRequest";
import {GameService} from "../../../services/game.service";
import {IApiResponseBase} from "../../../interfaces/IApiResponseBase";
import {CharityService} from "../../../services/charity.service";
import {ICharitiesByCategoryResult} from "../../../interfaces/charity/ICharitiesByCategoryResult";
import {GameTypeEnum} from "../../../enum/GameTypeEnum";
import {EditPaymentTiersFiftyFiftyComponent} from "../../edit-payment-tiers/edit-payment-tiers-fifty-fifty.component";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {
  EditPaymentTiersGoldrushComponent
} from "../../edit-payment-tiers-goldrush/edit-payment-tiers-goldrush.component";
import {MatError, MatFormField, MatHint} from "@angular/material/form-field";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {GameRulesService} from "../../../services/game-rules.service";
import {IGameRules} from "../../../interfaces/IGameRules";
import {MarkdownComponent} from "ngx-markdown";
import {MatInput} from "@angular/material/input";
import {IAddGameRules} from "../../../interfaces/IAddGameRules";
import {IUpdateGameRulesCommand} from "../../../interfaces/IUpdateGameRulesCommand";
import {ActiveUserService} from "../../../services/active-user.service";
import {MatCheckbox} from "@angular/material/checkbox";
import {AppConfigService} from "../../../services/app-config.service";
import {
  GameNotificationSettingsForm,
} from "../game-notification-settings-form/game-notification-settings-form.component";
import {IGameNotificationSettingsForm} from "../../../interfaces/new-game/forms/IGameNotificationForm";
import {IGameNotificationSettings} from "../../../interfaces/new-game/breakdown/IGameNotificationSettings";
import {MatTooltip} from "@angular/material/tooltip";
import {LotteryLicenceAddComponent} from "../../lottery-licences/lottery-licence-add/lottery-licence-add.component";
import {MatDialog} from "@angular/material/dialog";
import {IGameLotteryLicence} from "../../../interfaces/IGameLotteryLicence";
import {LotteryLicenceService} from "../../../services/lottery-licence.service";
import {LotteryLicenceEditComponent} from "../../lottery-licences/lottery-licence-edit/lottery-licence-edit.component";
import {
  DRAW_REMINDER_DELIVERY_INVALID_ERROR_TYPE,
  DRAW_REMINDER_DELIVERY_INVALID_MESSAGE,
  DRAW_REMINDER_TIMES_INVALID_ERROR_TYPE,
  DRAW_REMINDER_TIMES_INVALID_MESSAGE
} from "../../../consts/DrawReminderSettingsErrors";

// Mirrors Causable.Api.Data.Enum.ErrorTypes.SaveDraftGameNotFoundOrNotDraft.
const SAVE_DRAFT_GAME_NOT_A_DRAFT_ERROR_TYPE = 209;
// Mirrors Causable.Api.Data.Enum.ErrorTypes.FinalizeGameNotFoundOrNotDraft.
const FINALIZE_GAME_NOT_A_DRAFT_ERROR_TYPE = 929;
// Mirrors Causable.Api.Data.Enum.ErrorTypes.CreateAzureResourcesFailed.
const CREATE_AZURE_RESOURCES_FAILED_ERROR_TYPE = 185;
const GAME_NOT_A_DRAFT_MESSAGE =
  "This game is no longer a draft, so it can't be finalized. If its Azure resources are missing, create them from Manage Game > Create Azure Resources.";
const GAME_LIVE_WITHOUT_AZURE_RESOURCES_MESSAGE =
  'The game is now live, but its Azure resources were not created. Create them from Manage Game > Create Azure Resources.';

@Component({
  selector: 'app-new-game-container',
  standalone: true,
  imports: [
    AsyncPipe,
    GameAssetSettingsFormComponent,
    GameDrawSettingsComponentForm,
    GameMetadataSettingsFormComponent,
    GamePaymentSettingsFormComponent,
    GeneralGameSettingsForm,
    MatButton,
    MatFabButton,
    MatIcon,
    MatStep,
    MatStepLabel,
    MatStepper,
    EditPaymentTiersFiftyFiftyComponent,
    EditPaymentTiersGoldrushComponent,
    MatError,
    MatProgressSpinner,
    MarkdownComponent,
    MatFormField,
    MatHint,
    MatInput,
    ReactiveFormsModule,
    MatCheckbox,
    FormsModule,
    GameNotificationSettingsForm,
    MatTooltip,
    DatePipe
  ],
  templateUrl: './new-game-container.component.html',
  styleUrl: './new-game-container.component.css'
})
export class NewGameContainerComponent implements OnDestroy {

  // The cached game list is only refreshed at sign-in and finalize, so it can hold values older than the last save,
  // which the next save would write back. Refreshing it also returns the admin's full view of the game.
  @Input() set newGameIdSetter(newGameIdP: string) {
    this.gameService.getGamesForUser(this.activeUserService.activeUser().Id).pipe(
      map((gamesP) => gamesP.find((gameP) => gameP.Id === newGameIdP))
    ).subscribe({
      next: (draftGame) => {
        if (draftGame?.State !== GameStateEnum.Draft) {
          this.showWrongGameMessage = true;
          return;
        }

        this.inProgressDraftGame = draftGame;
        this.newGameId = newGameIdP;
        this.newGameService.mapToCompleteNewGameRequest(draftGame);
        this.draftLoaded = true;
        this.fetchNewGameData(newGameIdP);
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  private appConfigService: AppConfigService = inject(AppConfigService);
  private newGameService: NewGameService = inject(NewGameService);
  private gameService: GameService = inject(GameService);
  private gameRulesService: GameRulesService = inject(GameRulesService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private dateService: DateService = inject(DateService);
  private charityService: CharityService = inject(CharityService);
  private matDialog: MatDialog = inject(MatDialog);
  private lotteryLicenseService: LotteryLicenceService = inject(LotteryLicenceService);

  public gameGeneralForm!: FormGroup<IGameGeneralSettingsForm>;
  public gameDrawSettingsForm!: FormGroup<IGameDrawSettingsForm>;
  public gameNotificationSettingsForm!: FormGroup<IGameNotificationSettingsForm>
  public gamePaymentSettingsForm!: FormGroup<IGamePaymentSettingsForm>;
  public gameMetadataSettingsForm!: FormGroup<IGameMetadataSettingsForm>;
  public gameAssetsSettingsForm!: FormGroup<IGameAssetSettingsForm>;
  public newGame$: Observable<ICompleteNewGameRequest> = this.newGameService.selectNewGameRequest();
  public charitiesForNewGame$: Observable<ICharitiesByCategoryResult[]> = this.charityService.charityCategories$;
  public wrongGameIdMessage: string = 'Looks like the game you are trying to reach is not a draft game';
  public showWrongGameMessage: boolean = false;
  public gameRulesAlreadyExist: boolean = false;
  public existingGameRules: IGameRules | null = null;
  public validationErrors: string[] = [];
  protected readonly GameTypeEnum = GameTypeEnum;
  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public allowCreationOfAzureResources: boolean = this.appConfigService.allowCreationOfAzureResources();
  public createAzureResources: boolean = this.allowCreationOfAzureResources;
  public lotteryLicense: IGameLotteryLicence | null = null;

  public gameRulesControl: FormControl<string | null> = new FormControl<string | null>("", Validators.required);

  public gameRulesForm: FormGroup = new FormGroup({
    gameRules: this.gameRulesControl
  });

  private newGameId: string = '';
  public inProgressDraftGame: IGameQueryResult | null = null;

  public draftLoaded: boolean = false;
  public draftSavedAt: Date | null = null;
  public autosaveFailed: boolean = false;
  private hasUnsavedChanges: boolean = false;
  private autosaveStopped: boolean = false;

  // Every save, automatic or not, runs through this one queue, so an earlier save can never land after a later one.
  // It is not tied to the component's lifetime: leaving the page must not cancel a save already on its way.
  private saveQueue = new Subject<{ background: boolean, done: ReplaySubject<unknown> }>();
  private autosaveTriggers = new Subject<void>();

  constructor() {
    this.saveQueue.pipe(
      // Background saves still waiting when autosave stops (a refused session, a finalized game) are dropped.
      concatMap((requestP) => defer(() => this.autosaveStopped && requestP.background
        ? of(null)
        : this.getSaveGameRequest(requestP.background)).pipe(
        tap((resultP) => requestP.done.next(resultP)),
        catchError((errorP) => {
          requestP.done.error(errorP);
          return EMPTY;
        }),
        finalize(() => requestP.done.complete())))
    ).subscribe();

    this.autosaveTriggers.pipe(
      debounceTime(1500),
      takeUntilDestroyed(inject(DestroyRef))
    ).subscribe(() => this.autosaveNow());
  }

  @HostListener('window:beforeunload', ['$event'])
  warnBeforeLeavingWithUnsavedChanges(eventP: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges && !this.autosaveStopped) {
      eventP.preventDefault();
    }
  }

  ngOnDestroy() {
    if (this.hasUnsavedChanges && !this.autosaveStopped) {
      this.autosaveNow();
    }
    this.saveQueue.complete();
  }

  onDraftEdited() {
    this.hasUnsavedChanges = true;
    this.autosaveDraft();
  }

  autosaveDraft() {
    if (!this.autosaveStopped) {
      this.autosaveTriggers.next();
    }
  }

  private autosaveNow() {
    if (this.autosaveStopped || !this.draftLoaded) {
      return;
    }

    this.queueSave(true).subscribe({
      next: (saveResultP) => {
        if (saveResultP != null) {
          this.draftSavedAt = new Date();
          this.autosaveFailed = false;
        }
      },
      error: (errorP) => this.handleAutosaveFailure(errorP)
    });
  }

  private handleAutosaveFailure(errorP: any) {
    // Signing in again is already under way, and the game is no longer a draft: neither is a save the admin can make.
    if (errorP?.Status === 401 || errorP?.Status === 403 || errorP?.Error?.ErrorType === SAVE_DRAFT_GAME_NOT_A_DRAFT_ERROR_TYPE) {
      this.autosaveStopped = true;
      return;
    }

    this.hasUnsavedChanges = true;
    this.autosaveFailed = true;
  }

  private queueSave(backgroundP: boolean): Observable<unknown> {
    const done = new ReplaySubject<unknown>(1);
    this.hasUnsavedChanges = false;
    this.saveQueue.next({background: backgroundP, done});
    return done;
  }

  formatMarkdown(textP: string) {
    if (textP == undefined) {
      return "";
    }
    return textP.replace(/\n/, "<br>");
  }

  private fetchNewGameData(newGameIdP: string) {
    forkJoin([
      this.charityService.fetchCharitiesPerGameByCategory(newGameIdP),
      this.gameRulesService.getGameRules(newGameIdP),
      this.lotteryLicenseService.fetchLotteryLicences(newGameIdP)
    ]).subscribe({
      next: ([_charities, gameRulesP, lotteryLicenses]) => {
        if (gameRulesP.GameRulesContent && gameRulesP.Id) {
          this.existingGameRules = gameRulesP;
          this.gameRulesAlreadyExist = true;
          this.gameRulesControl.setValue(gameRulesP.GameRulesContent);
        }

        const activeLicense = lotteryLicenses.find(license => license.Active);
        if (activeLicense) {
          this.lotteryLicense = activeLicense;
        }
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  addNewCharities() {
    this.charityService.openAddCharityDialog(this.newGameId);
  }

  addNewGameRules() {
    this.uiState = UIStateEnum.ShowLoading;
    let newRules: IAddGameRules = {
      GameId: this.newGameId,
      AdminId: this.activeUserService.activeUser().Id,
      GameRulesContent: this.gameRulesControl.value ?? ''
    };

    this.gameRulesService.addGameRules(newRules).subscribe({
      next: (newRulesP) => {
        this.existingGameRules = newRulesP;
        this.gameRulesAlreadyExist = true;
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openSuccessfulSnackBar('Added new game rules.')
      }, error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  editGameRules() {
    if (!this.gameRulesForm.valid) {
      this.gameRulesForm.markAllAsTouched();
      return;
    }

    let command: IUpdateGameRulesCommand = {
      gameId: this.newGameId,
      adminId: this.activeUserService.activeUser().Id,
      GameRules: {
        GameRulesContent: this.gameRulesControl.value!,
        GameId: this.newGameId,
        Id: this.existingGameRules?.Id!
      }
    }

    this.gameRulesService.updateGameRules(command).subscribe({
      next: () => {
        this.snackbarService.openSuccessfulSnackBar("Successfully updated the game rules!");
      },
      error: err => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  saveGame() {
    this.queueSave(false).subscribe({
        next: () => {
          this.draftSavedAt = new Date();
          this.autosaveFailed = false;
          this.snackbarService.openSuccessfulSnackBar('New Game Saved.')
        }, error: (err) => {
          this.errorHandlingService.displayPageLevelErrorMessage(err);
        }
      }
    )
  }

  private getSaveGameRequest(backgroundP: boolean): Observable<any> {
    this.newGameService.saveNewGameGeneral(this.formToGeneralGameSettingsMapper());
    this.newGameService.saveNewGameDraw(this.formToDrawGameSettingsMapper());
    this.newGameService.saveNewGameNotification(this.formToNotificationSettingsMapper());
    this.newGameService.saveNewGamePayment(this.formToPaymentGameSettingsMapper());
    this.newGameService.saveNewGameAssets(this.formToAssetGameSettingsMapper());
    this.newGameService.saveNewGameMeta(this.formToMetadataGameSettingsMapper());

    const newGameSaveRequest = this.newGameService.mapGameToRequest();
    if (newGameSaveRequest) {
      return this.newGameService.saveGameToApi(newGameSaveRequest, backgroundP);
    }

    return of(null);
  }

  private formToMetadataGameSettingsMapper(): IGameMetadataSettings {
    const controls = this.gameMetadataSettingsForm.value;
    return {
      metadataDescription: controls.metadataDescription ?? '',
      metadataImageUrl: controls.metadataImageUrl ?? '',
      metadataSiteName: controls.metadataSiteName ?? '',
      metadataTitle: controls.metadataTitle ?? '',
      metadataUrl: controls.metadataUrl ?? '',
      metadataType: controls.metadataType ?? '',
      metadataFaviconUrl: controls.metadataFaviconUrl ?? ''
    };
  }

  private formToPaymentGameSettingsMapper(): IGamePaymentSettings {
    const controls = this.gamePaymentSettingsForm.value;
    return {
      causableFeeMultiplier: controls.causableFeeMultiplier ? controls.causableFeeMultiplier / 100 : 0,
      deductTaxesFromCharityContribution: controls.deductTaxesFromCharityContribution ?? true,
      stripePublicKey: controls.stripePublicKey ?? '',
      administrationCommissionMultiplier: controls.administrationCommissionMultiplier ? controls.administrationCommissionMultiplier / 100 : 0
    };
  }

  private formToAssetGameSettingsMapper(): IGameAssetSettings {
    const controls = this.gameAssetsSettingsForm.value;
    return {
      gameThemeTypeId: controls.gameThemeTypeId ?? 0,
      primaryGameColor: controls.primaryGameColor ?? '',
      charityRegistrationUrl: controls.charityRegistrationUrl || null
    };
  }

  private formToDrawGameSettingsMapper(): IGameDrawSettings {
    const controls = this.gameDrawSettingsForm.value;
    return {
      drawDayOfWeek: controls.drawDayOfWeek ?? 0,
      drawTimeMinutesFromMidnightLocal: controls.drawTimeMinutesFromMidnightLocal ? this.dateService.convertMilitaryTimeToMinutes(controls.drawTimeMinutesFromMidnightLocal) : 0,
      manualDraw: controls.manualDraw ?? false,
      maxTicketsPerDraw: controls.maxTicketsPerDraw ?? 0,
      maxPrizeAmount: controls.maxPrizeAmount ? controls.maxPrizeAmount * 100 : null,
      instanceIntervalType: controls.instanceIntervalType ?? 0,
      minimumGameLengthInHours: controls.minimumGameLengthInHours ?? 0,
      maxAmountPlayersCanSpendPerDraw: controls.maxAmountPlayersCanSpendPerDraw ? controls.maxAmountPlayersCanSpendPerDraw * 100 : 0,
    };
  }

  private formToNotificationSettingsMapper(): IGameNotificationSettings {
    const controls = this.gameNotificationSettingsForm.value;

    return {
      notificationsBeginMinutesBeforeDraw: controls.notificationsBeginMinutesBeforeDraw && this.gameDrawSettingsForm.value.drawTimeMinutesFromMidnightLocal ?
        this.dateService.convertMilitaryTimeToMinutes(this.gameDrawSettingsForm.value.drawTimeMinutesFromMidnightLocal) -
        this.dateService.convertMilitaryTimeToMinutes(controls.notificationsBeginMinutesBeforeDraw) : 0,
      notificationsEndMinutesBeforeDraw: controls.notificationsEndMinutesBeforeDraw && this.gameDrawSettingsForm.value.drawTimeMinutesFromMidnightLocal ?
        this.dateService.convertMilitaryTimeToMinutes(this.gameDrawSettingsForm.value.drawTimeMinutesFromMidnightLocal) -
        this.dateService.convertMilitaryTimeToMinutes(controls.notificationsEndMinutesBeforeDraw) : 0,
      notificationFrequency: controls.notificationFrequency ?? 0,
      notificationChunkSize: controls.notificationChunkSize ?? 0,
      sendLastFourDigitsOfWinningCreditCardToOrganization: controls.sendLastFourDigits ?? false
    };
  }

  private formToGeneralGameSettingsMapper(): IGameGeneralSettings {

    const controls = this.gameGeneralForm.value;
    return {
      type: controls.type ?? 0,
      dateOfFirstInstance: controls.dateOfFirstInstance ?? new Date(),
      dateOfFinalInstance: controls.dateOfFinalInstance ?? null,
      gameDescription: controls.gameDescription ?? '',
      autoplayEnabled: controls.autoplayEnabled ?? false,
      regionLockPlayerGeolocation: controls.regionLockPlayerGeolocation ?? false,
      regionLockPlayerPostalCode: controls.regionLockPlayerPostalCode ?? false,
      timezone: controls.timezone ?? '',
      collectPlayerAddress: controls.collectPlayerAddress ?? false,
      senderEmail: controls.senderEmail ?? '',
      pushNotificationMessageFeeCost: controls.pushNotificationMessageFeeCost ?? 0,
      maxReceiptAttempts: controls.maxReceiptAttempts ?? 0,
      displayNonWinnerName: controls.displayNonWinnerName ?? false,
      allowFreeTickets: controls.allowFreeTickets ?? false,
      contactEmail: controls.contactEmail ?? '',
      twilioAuthToken: controls.twilioAuthToken ?? '',
      twilioPhoneNumber: controls.twilioPhoneNumber ?? '',
      randomApiOverrideId: controls.randomApiOverrideId ?? '',
      twilioSid: controls.twilioSid ?? '',
      maxPledgesPerPlayer: controls.maxPledgesPerPlayer ?? 0,
      shortName: controls.shortName ?? '',
      gameMoreInformation: controls.gameMoreInformation ?? '',
      minimumAgeOfParticipation: controls.minimumAgeOfParticipation ?? 19,
      stripeAccountType: controls.stripeAccountType ?? 1,
      regionInfo: controls.regionInfo ?? '',
      numberOfLoginAttemptsBeforeLockout: controls.numberOfLoginAttemptsBeforeLockout ?? 5
    };
  }

  onValidateGameClick() {
    this.validationErrors = [];

    this.uiState = UIStateEnum.ShowRequestProcessing;
    this.queueSave(false).pipe(
      concatMap((saveRes) => {
        if (saveRes != null) {
          return this.newGameService.completeGameAndValidate(this.createAzureResources);
        }
        return of(null);
      })).subscribe({
      next: (validateResP) => {
        if (validateResP != null) {
          this.autosaveStopped = true;
          this.uiState = UIStateEnum.ShowData;
          this.snackbarService.openSuccessfulSnackBar('New game has been saved and new game is valid!.')
        } else {
          this.uiState = UIStateEnum.ShowData;
          this.snackbarService.openErrorSnackBar('Something went wrong validating your game.')
        }

      }, error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        let errors = err.Error as IApiResponseBase;
        this.validationErrors = errors.Errors ?? [];
        if (errors.ErrorType === DRAW_REMINDER_TIMES_INVALID_ERROR_TYPE) {
          this.validationErrors = [...this.validationErrors, DRAW_REMINDER_TIMES_INVALID_MESSAGE];
        } else if (errors.ErrorType === DRAW_REMINDER_DELIVERY_INVALID_ERROR_TYPE) {
          this.validationErrors = [...this.validationErrors, DRAW_REMINDER_DELIVERY_INVALID_MESSAGE];
        } else if (errors.ErrorType === CREATE_AZURE_RESOURCES_FAILED_ERROR_TYPE) {
          this.autosaveStopped = true;
          this.validationErrors = [...this.validationErrors, GAME_LIVE_WITHOUT_AZURE_RESOURCES_MESSAGE];
        } else if (errors.ErrorType === FINALIZE_GAME_NOT_A_DRAFT_ERROR_TYPE || errors.ErrorType === SAVE_DRAFT_GAME_NOT_A_DRAFT_ERROR_TYPE) {
          // Finalize saves first, so on a live game it is usually the save that refuses.
          this.autosaveStopped = true;
          this.validationErrors = [...this.validationErrors, GAME_NOT_A_DRAFT_MESSAGE];
        }
      }
    })
  }

  openAddLicenceNumber() {
    this.matDialog.open(LotteryLicenceAddComponent, {
      data: this.inProgressDraftGame,
      width: '650px'
    }).afterClosed().subscribe({
      next: () => {
        this.fetchNewGameData(this.newGameId);
      },
      error: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openErrorSnackBar("Failed to add lottery license to game");
      }
    });
  }

  openEditLicenceNumber()
  {
    this.matDialog.open(LotteryLicenceEditComponent, {
      data: this.lotteryLicense,
      width: '650px'
    }).afterClosed().subscribe({
      next: () => {
        this.fetchNewGameData(this.newGameId);
      },
      error: () => {
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openErrorSnackBar("Failed to edit lottery license");
      }
    })
  }

  protected readonly uiStateEnumForTemplate = UIStateEnum;
}
